import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";

interface Item {
  id: string;
  name: string;
  [key: string]: any;
}

interface MultiSelectProps {
  items: Item[];
  selectedIds: string[];
  onToggleItem: (id: string) => void;
  placeholder: string;
  emptyMessage: string;
  searchPlaceholder: string;
  disabled?: boolean;
  maxHeight?: string;
  className?: string;
}

export function MultiSelect({
  items,
  selectedIds,
  onToggleItem,
  placeholder,
  emptyMessage,
  searchPlaceholder,
  disabled = false,
  maxHeight = "60",
  className = "",
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Count selected items for display
  const selectedCount = selectedIds.length; 
    
  // Get a display string for selected items
  const selectedText = selectedCount > 0
    ? `${selectedCount} selected` 
    : placeholder;

  // Filter items based on search query with improved filtering
  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase().trim();
    
    return items.filter(item => {
      // Handle potentially malformed or null item names
      if (!item.name) return false;
      
      // Normalize the item name: trim whitespace and lowercase
      const normalizedName = item.name.toLowerCase().trim();
      
      // Check if the normalized name includes the query
      return normalizedName.includes(query);
    });
  }, [items, searchQuery]);

  return (
    <div className={className}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div 
            role="combobox" 
            aria-expanded={isOpen} 
            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            <span className={selectedIds.length === 0 ? 'text-muted-foreground' : 'text-card-foreground'}>
                {selectedText}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[--trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} value={searchQuery} onValueChange={setSearchQuery} />
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup className={`max-h-${maxHeight} overflow-y-auto`}>
              {filteredItems.map((item) => (
                <CommandItem 
                  key={item.id} 
                  value={item.name} 
                  onSelect={() => onToggleItem(item.id)}
                >
                  <Checkbox 
                    className="mr-2 h-4 w-4" 
                    checked={selectedIds.includes(item.id)} 
                  />
                  {item.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
} 